import React, { useState } from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Cell,
} from 'recharts';
import { TrendingUp, Clock, DollarSign, Users, CheckCircle } from 'lucide-react';
import { IMPROVEMENTS } from './marketData';

const PriorityBadge = ({ priority }) => {
  const colors = {
    Critical: 'bg-red-600 text-white',
    High:     'bg-amber-500 text-white',
    Medium:   'bg-violet-500 text-white',
    Low:      'bg-slate-200 text-slate-600',
  };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${colors[priority] || 'bg-slate-100 text-slate-600'}`}>{priority}</span>;
};

const CategoryBadge = ({ cat }) => (
  <span className="text-[10px] border border-slate-200 text-slate-500 px-2 py-0.5 rounded-full">{cat}</span>
);

const ScatterTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-slate-800 border border-white/10 rounded-xl p-3 shadow-xl text-xs max-w-[200px]">
      <p className="text-white font-bold mb-1">{d.title}</p>
      <p className="text-indigo-300">Impact: {d.impact}/100</p>
      <p className="text-amber-300">Effort: {d.effort}/100</p>
      <p className="text-slate-300 mt-1">{d.timeline}</p>
    </div>
  );
};

const SORT_OPTIONS = ['Priority', 'Impact ↓', 'Effort ↑', 'Category'];

export default function ImprovementReport() {
  const [sort, setSort]         = useState('Priority');
  const [filterPri, setFilter]  = useState('All');
  const priorities = ['All', 'Critical', 'High', 'Medium', 'Low'];

  const sorted = [...IMPROVEMENTS]
    .filter(i => filterPri === 'All' || i.priority === filterPri)
    .sort((a, b) => {
      if (sort === 'Impact ↓')  return b.impact - a.impact;
      if (sort === 'Effort ↑')  return a.effort - b.effort;
      if (sort === 'Category')  return a.category.localeCompare(b.category);
      // Priority order
      const ORDER = { Critical: 0, High: 1, Medium: 2, Low: 3 };
      return (ORDER[a.priority] ?? 4) - (ORDER[b.priority] ?? 4);
    });

  const critical = IMPROVEMENTS.filter(i => i.priority === 'Critical').length;
  const high     = IMPROVEMENTS.filter(i => i.priority === 'High').length;
  const avgImpact = Math.round(IMPROVEMENTS.reduce((s, i) => s + i.impact, 0) / IMPROVEMENTS.length);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Improvement Roadmap</h1>
        <p className="text-sm text-slate-500 mt-1">
          {IMPROVEMENTS.length} prioritised recommendations · ranked by impact and strategic value
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-red-50 border border-red-100 rounded-2xl px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Critical Priority</p>
            <p className="text-2xl font-black text-red-600">{critical}</p>
          </div>
          <TrendingUp size={22} className="text-red-400" />
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-2xl px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">High Priority</p>
            <p className="text-2xl font-black text-amber-600">{high}</p>
          </div>
          <TrendingUp size={22} className="text-amber-400" />
        </div>
        <div className="bg-indigo-50 border border-indigo-100 rounded-2xl px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Avg Impact Score</p>
            <p className="text-2xl font-black text-indigo-600">{avgImpact}/100</p>
          </div>
          <DollarSign size={22} className="text-indigo-400" />
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Total Items</p>
            <p className="text-2xl font-black text-emerald-600">{IMPROVEMENTS.length}</p>
          </div>
          <CheckCircle size={22} className="text-emerald-400" />
        </div>
      </div>

      {/* Priority Matrix scatter */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-700 mb-1">Impact vs Effort Priority Matrix</h2>
        <p className="text-xs text-slate-400 mb-4">Top-left = quick wins · Top-right = big bets · Bottom = deprioritise</p>
        <ResponsiveContainer width="100%" height={300}>
          <ScatterChart margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              type="number" dataKey="effort" name="Effort" domain={[0, 100]}
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              label={{ value: 'Implementation Effort →', position: 'insideBottom', offset: -5, fontSize: 10, fill: '#94a3b8' }}
            />
            <YAxis
              type="number" dataKey="impact" name="Impact" domain={[40, 100]}
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              label={{ value: 'Business Impact ↑', angle: -90, position: 'insideLeft', offset: 10, fontSize: 10, fill: '#94a3b8' }}
            />
            <ReferenceLine x={50} stroke="#e2e8f0" strokeDasharray="4 2" />
            <ReferenceLine y={70} stroke="#e2e8f0" strokeDasharray="4 2" />
            <Tooltip content={<ScatterTooltip />} />
            <Scatter data={IMPROVEMENTS} name="Items">
              {IMPROVEMENTS.map((item, i) => (
                <Cell key={i} fill={item.color} opacity={0.85} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs text-center text-slate-500">
          <div className="bg-emerald-50 rounded-lg py-1.5 px-2">
            <p className="font-bold text-emerald-700">Quick Wins</p>
            <p>High impact · Low effort</p>
          </div>
          <div className="bg-indigo-50 rounded-lg py-1.5 px-2">
            <p className="font-bold text-indigo-700">Big Bets</p>
            <p>High impact · High effort</p>
          </div>
          <div className="bg-slate-50 rounded-lg py-1.5 px-2">
            <p className="font-bold text-slate-600">Fill-ins</p>
            <p>Low impact · Low effort</p>
          </div>
          <div className="bg-red-50 rounded-lg py-1.5 px-2">
            <p className="font-bold text-red-600">Avoid / Defer</p>
            <p>Low impact · High effort</p>
          </div>
        </div>
      </div>

      {/* Filters & sort */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap">
          {priorities.map(p => (
            <button key={p} onClick={() => setFilter(p)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filterPri === p ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
              {p}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Sort:</span>
          {SORT_OPTIONS.map(s => (
            <button key={s} onClick={() => setSort(s)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${sort === s ? 'bg-slate-700 text-white border-slate-700' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Recommendation cards */}
      <div className="space-y-4">
        {sorted.map((item, idx) => (
          <div key={item.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="flex items-start gap-4 p-5">
              {/* Number badge */}
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-white font-black text-sm" style={{ background: item.color }}>
                {item.id}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="text-sm font-bold text-slate-800">{item.title}</h3>
                      <PriorityBadge priority={item.priority} />
                      <CategoryBadge cat={item.category} />
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">{item.description}</p>
                  </div>
                  <div className="flex gap-3 shrink-0">
                    <div className="text-center">
                      <p className="text-lg font-black" style={{ color: item.color }}>{item.impact}</p>
                      <p className="text-[10px] text-slate-400 leading-tight">Impact</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-black text-slate-500">{item.effort}</p>
                      <p className="text-[10px] text-slate-400 leading-tight">Effort</p>
                    </div>
                  </div>
                </div>

                {/* Impact bars */}
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div>
                    <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                      <span>Impact Score</span><span>{item.impact}/100</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full" style={{ width: `${item.impact}%`, background: item.color }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                      <span>Implementation Effort</span><span>{item.effort}/100</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full bg-slate-400" style={{ width: `${item.effort}%` }} />
                    </div>
                  </div>
                </div>

                {/* Meta row */}
                <div className="flex items-center gap-4 mt-3 flex-wrap text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <Clock size={11} />{item.timeline}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users size={11} />{item.competitors}/10 competitors have this
                  </span>
                  <span className="flex items-center gap-1">
                    <DollarSign size={11} />{item.revenue_impact}
                  </span>
                </div>

                {/* Benefits */}
                <div className="mt-3 flex gap-2 flex-wrap">
                  {item.benefits.map((b, bi) => (
                    <span key={bi} className="text-[10px] bg-slate-50 border border-slate-100 text-slate-500 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <CheckCircle size={9} className="text-emerald-500 shrink-0" />{b}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom CTA */}
      <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl p-6 text-white">
        <h2 className="text-lg font-bold mb-2">Recommended First 90 Days</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {IMPROVEMENTS.filter(i => ['Critical','High'].includes(i.priority) && i.effort < 40).slice(0,3).map((item, i) => (
            <div key={i} className="bg-white/10 rounded-xl p-3 border border-white/20">
              <p className="text-xs font-semibold mb-1">{item.title}</p>
              <p className="text-[11px] text-white/70">{item.timeline} · Impact {item.impact}/100</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-white/60 mt-4">
          These quick wins deliver high business impact with relatively low implementation effort — ideal starting points.
        </p>
      </div>
    </div>
  );
}
